import { normalizeRoleSlug } from "../../../public/utils/roleAccess";
import { isOfficeRoleSlug, isReceptionRoleSlug } from "./visitorRoleSlugs";
import {
  VISITOR_CONTROL_SUBMODULE_SLUG,
  VISITOR_LIST_SUBMODULE_SLUG,
  VISITOR_REGISTER_SUBMODULE_SLUG,
} from "./visitorPermissionSlugs";

function submodulePerm(permMap, slug, type) {
  if (!permMap || !slug) return false;
  const row = permMap[slug];
  if (!row || typeof row !== "object") return false;
  return Boolean(row[type]);
}

function anySubmodule(permMap, slugs, type) {
  return slugs.some((slug) => submodulePerm(permMap, slug, type));
}

const STAFF_VISITOR_SLUGS = [
  VISITOR_LIST_SUBMODULE_SLUG,
  VISITOR_REGISTER_SUBMODULE_SLUG,
];

function permRow(permMap, slug) {
  const row = permMap?.[slug];
  return row && typeof row === "object" ? row : {};
}

function staffDirectoryStylePerms(row, { isOffice, isReceptionist }) {
  const canView = Boolean(row.view);
  const canEdit = Boolean(row.edit);
  const canDelete = Boolean(row.delete);
  return {
    canView,
    canCreate: false,
    canFullEdit: canEdit && !isReceptionist,
    canLimitedEdit: canEdit && isReceptionist,
    canEdit,
    canExit: canEdit,
    canSlip: canView,
    canDelete,
    canManageMasters: false,
    canDeleteMasters: false,
    canExport: canView,
    isAdmin: false,
    isOffice,
    isReceptionist,
  };
}

function staffRegisterStylePerms(row, { isOffice, isReceptionist }) {
  const canView = Boolean(row.view);
  const canCreate = Boolean(row.create);
  const canEdit = Boolean(row.edit);
  const canDelete = Boolean(row.delete);
  return {
    canView: canView || canCreate,
    canCreate,
    canFullEdit: canEdit && !isReceptionist,
    canLimitedEdit: canEdit && isReceptionist,
    canEdit,
    canExit: canEdit,
    canSlip: canCreate || canView,
    canDelete,
    canManageMasters: false,
    canDeleteMasters: false,
    canExport: canView || canCreate,
    isAdmin: false,
    isOffice,
    isReceptionist,
  };
}

function staffRoleFlags(roleSlug) {
  return {
    isOffice: isOfficeRoleSlug(roleSlug),
    isReceptionist: isReceptionRoleSlug(roleSlug),
  };
}

/**
 * Permissions for one visitor screen (User builder submodule row).
 * All roles — including admin — follow User builder only.
 */
export function buildVisitorPermissionsForScreen(
  permMap = {},
  roleSlug = "",
  screenSlug = "",
) {
  const slug = normalizeRoleSlug(screenSlug);
  const { isOffice, isReceptionist } = staffRoleFlags(roleSlug);
  const row = permRow(permMap, slug);
  const canView = Boolean(row.view);
  const canEdit = Boolean(row.edit);
  const canDelete = Boolean(row.delete);

  if (slug === VISITOR_CONTROL_SUBMODULE_SLUG) {
    return {
      canView,
      canCreate: false,
      canFullEdit: canEdit && !isReceptionist,
      canLimitedEdit: canEdit && isReceptionist,
      canEdit,
      canExit: canEdit,
      canSlip: canView,
      canDelete,
      canManageMasters: canView && canEdit,
      canDeleteMasters: canDelete,
      canExport: canView,
      isAdmin: canView,
      isOffice,
      isReceptionist,
    };
  }

  if (slug === VISITOR_REGISTER_SUBMODULE_SLUG) {
    return staffRegisterStylePerms(row, { isOffice, isReceptionist });
  }

  if (slug === VISITOR_LIST_SUBMODULE_SLUG) {
    return staffDirectoryStylePerms(row, { isOffice, isReceptionist });
  }

  return buildVisitorPermissions(permMap, roleSlug);
}

/**
 * Visitor UI permissions from User builder only (no role-name bypass).
 * List rows are scoped per logged-in user on the API.
 */
export function buildVisitorPermissions(permMap = {}, roleSlug = "") {
  const isOffice = isOfficeRoleSlug(roleSlug);
  const isReceptionist = isReceptionRoleSlug(roleSlug);

  const canView = anySubmodule(
    permMap,
    [...STAFF_VISITOR_SLUGS, VISITOR_CONTROL_SUBMODULE_SLUG],
    "view",
  );
  const canCreate = anySubmodule(permMap, STAFF_VISITOR_SLUGS, "create");
  const canEdit = anySubmodule(
    permMap,
    [...STAFF_VISITOR_SLUGS, VISITOR_CONTROL_SUBMODULE_SLUG],
    "edit",
  );
  const canDelete = anySubmodule(
    permMap,
    [...STAFF_VISITOR_SLUGS, VISITOR_CONTROL_SUBMODULE_SLUG],
    "delete",
  );
  const canControl = submodulePerm(permMap, VISITOR_CONTROL_SUBMODULE_SLUG, "view");
  const controlRow = permRow(permMap, VISITOR_CONTROL_SUBMODULE_SLUG);

  return {
    canView,
    canCreate,
    canFullEdit: canEdit && !isReceptionist,
    canLimitedEdit: canEdit && isReceptionist,
    canEdit,
    canExit: canEdit,
    canSlip: canCreate || canView,
    canDelete,
    canManageMasters: canControl && Boolean(controlRow.edit),
    canDeleteMasters: canControl && Boolean(controlRow.delete),
    canExport: canView && submodulePerm(permMap, VISITOR_LIST_SUBMODULE_SLUG, "view"),
    isAdmin: canControl,
    isOffice,
    isReceptionist,
  };
}
